import React, { useCallback, useRef, useState } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { isEqual, some } from 'lodash-es';

import ConfirmModal from './Modals/ConfirmModal';
import Tooltip from '../../Tooltip';

import { DATA_TYPE, DEFAULT_REPORT, permissions } from '../configs/constants';

const options = [{
  value: DATA_TYPE.BASE,
  label: 'Base Data',
  description: 'Core app performance metrics across 3,500+ public companies.',
}, permissions?.allowAdvancedMetricsExport && {
  value: DATA_TYPE.ADVANCED,
  label: 'Advanced Data',
  description: 'Segmented behavioral insights from 15M+ real-user devices.',
}, permissions?.allowMpi && {
  value: DATA_TYPE.MPI,
  label: 'MPI',
  description: 'Apptopia‑curated metrics highly correlated to core company KPIs.',
  tooltip: 'Mobile Performance Index',
}].filter(Boolean);

const isReportChanged = (prevReport, nextReport) => {
  const excludedFields = ['id', 'name'];

  return some(prevReport, (value, key) => {
    if (excludedFields.includes(key)) {
      return false;
    }

    return !isEqual(value, nextReport[key]);
  });
};

const DataTypeSwitcher = ({
  value,
  dataType,
  report,
  onChange,
}) => {
  const memoNextValue = useRef();
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);

  const handleChange = (nextValue) => {
    const shouldShowConfirmModal = isReportChanged(DEFAULT_REPORT[dataType], report);

    if (shouldShowConfirmModal) {
      memoNextValue.current = nextValue;
      setConfirmModalOpen(true);
    } else {
      onChange(nextValue);
    }
  };

  const handleConfirm = useCallback(() => {
    setConfirmModalOpen(false);
    onChange(memoNextValue.current);
  }, []);

  const handleClose = useCallback(() => {
    setConfirmModalOpen(false);
  }, []);

  return (
    <>
      <section className="upsert-export-form__data-type align-items-center">
        <div className="upsert-export-form__data-type-title">
          <h4 className="h4">Data type</h4>
        </div>
        <div className="upsert-export-form__data-type__content">
          <div className="data-type-switcher">
            {options.map((option) => {
              const isActive = option.value === value;

              return (
                <div
                  key={option.value}
                  className={cx('data-type-switcher__option', { active: isActive })}
                  onClick={() => !isActive && handleChange(option.value)}
                >
                  <h4 className="h5 data-type-switcher__option-label">
                    {option.label}
                    {option.tooltip && (
                      <Tooltip content={option.tooltip}>
                        <span className="hicon-question-circle text-16 text-quiet" />
                      </Tooltip>
                    )}
                  </h4>
                  <p className="data-type-switcher__option-description">{option.description}</p>
                </div>
              );
            })}
          </div>
        </div>
        {/* <div className="upsert-export-form__data-type-warning">
          <i className="hicon-warning" />
          <p className="data-type-switcher__warning-text">
            Changing data types will result in all Export Settings to be lost (date range and entities will be retained)
          </p>
        </div> */}
      </section>

      {isConfirmModalOpen && (
        <ConfirmModal
          title="Change export type?"
          description={(
            <>You will lose current selections and settings. <br /> This cannot be undone.</>
          )}
          confirmButtonContent="Change export type"
          onConfirm={handleConfirm}
          onClose={handleClose}
        />
      )}
    </>
  );
};

DataTypeSwitcher.propTypes = {
  value: T.string.isRequired,
  dataType: T.string.isRequired,
  report: T.object.isRequired,
  onChange: T.func.isRequired,
};

export default DataTypeSwitcher;
